import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter, getMaxDateForAge } from '@repo/toolkit/date'
import { MIN_AGE_REQUIRED } from '@repo/toolkit/min-age-required'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { isBefore } from 'date-fns'
import { useFormContext } from 'react-hook-form'

import { getNationalitiesList } from '../../../api/countries/get-nationalities-list'
import useTranslate from '../../../lib/hooks/use-translate'
import {
  MaritalStatusEnum,
  maritalStatusEnumMapping
} from '../../../lib/types/enums/staff-application/marital-status.enum'
import {
  PositionStatusEnum,
  positionStatusEnumMapping
} from '../../../lib/types/enums/staff-application/position-status.enum'
import { HostFamilyInput } from '../../../lib/types/models/host-family/host-family-input'
import {
  getSelectedEnumOption,
  getSelectedOption
} from '../../../lib/utils/functions/misc.functions'
import { FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledDateStringInput from '../../elements/inputs/controlled-date-string-input'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledPhoneInput from '../../elements/inputs/controlled-phone-input'
import ControlledRadioInput from '../../elements/inputs/controlled-radio-input'
import ControlledSelectInput from '../../elements/inputs/controlled-select-input'

type ParentsFormProps = {
  parentNumber: '1' | '2'
  required?: boolean
}

const ParentsForm = ({ parentNumber, required = false }: ParentsFormProps) => {
  const form = useFormContext<HostFamilyInput>()
  const { t } = useTranslate()
  const birthDate = form.watch(`parent${parentNumber}DateOfBirth`)

  const { data: nationalitiesList } = useQuery({
    queryKey: ['nationalities-list'],
    queryFn: getNationalitiesList
  })

  return (
    <>
      <Typography variant="tag" color={tailwindColors.secondary.DEFAULT}>
        {parentNumber === '1' ? 'Wife' : 'Husband'}
      </Typography>
      <div className="h-4" />
      <div className="flex flex-col gap-3">
        <div className="flex gap-3">
          <FormField
            control={form.control}
            name={`parent${parentNumber}LastName`}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} type="text" label="Last name" required={required} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`parent${parentNumber}FirstName`}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} type="text" label="First name" required={required} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name={`parent${parentNumber}Nationality`}
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledSelectInput
                  {...field}
                  searchable
                  label="Nationality"
                  required={required}
                  options={nationalitiesList?.map((nationality) => ({
                    label: nationality.label,
                    value: nationality.code
                  }))}
                  selectedOption={getSelectedOption(
                    field.value,
                    nationalitiesList?.find((national) => national.code === field.value)?.label
                  )}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <div className="flex gap-3">
          <FormField
            control={form.control}
            name={`parent${parentNumber}PhoneNumber`}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledPhoneInput {...field} label="Phone number" defaultCountry="FR" />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`parent${parentNumber}Email`}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledInput {...field} type="email" label="E-mail" required={required} />
                </FormControl>
              </FormItem>
            )}
          />
        </div>
        <div className="flex gap-3">
          <FormField
            control={form.control}
            name={`parent${parentNumber}DateOfBirth`}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledDateStringInput
                    {...field}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    type="birthdate"
                    label="Date of birth"
                    required={required}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <div className="bg-white-dark p-3 rounded flex items-center justify-center cursor-not-allowed">
            <Typography variant="label-medium" color={tailwindColors.grey.lighter}>
              {!birthDate || isBefore(getMaxDateForAge(MIN_AGE_REQUIRED), birthDate)
                ? 'Age'
                : DateFormatter.calculateAge(birthDate)}
            </Typography>
          </div>
        </div>
        <FormField
          control={form.control}
          name={`parent${parentNumber}Position`}
          render={({ field }) => (
            <FormItem className="flex-1">
              <FormControl>
                <ControlledInput {...field} type="text" label="Profession" required={required} />
              </FormControl>
            </FormItem>
          )}
        />
        <div className="flex gap-4">
          {Object.values(PositionStatusEnum).map((position) => (
            <FormField
              key={position}
              control={form.control}
              name={`parent${parentNumber}PositionStatus`}
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormControl>
                      <ControlledRadioInput
                        {...field}
                        label={t(positionStatusEnumMapping[position])}
                        checked={field.value === position}
                        onCheck={() => field.onChange(position)}
                      />
                    </FormControl>
                  </FormItem>
                )
              }}
            />
          ))}
        </div>
      </div>
    </>
  )
}

const HostFamilyParentsInformationForm = () => {
  const { t } = useTranslate()
  const form = useFormContext<HostFamilyInput>()

  return (
    <InnerBox className="p-4 rounded-xl">
      <Typography variant="text" color={tailwindColors.primary.DEFAULT}>
        Parents' information
      </Typography>
      <div className="h-4" />
      <ParentsForm parentNumber="1" required />
      <div className="h-4" />
      <ParentsForm parentNumber="2" />
      <div className="h-4" />

      <FormField
        control={form.control}
        name="maritalStatus"
        render={({ field }) => (
          <FormItem className="w-full">
            <FormControl>
              <ControlledSelectInput
                {...field}
                required
                label="Marital status"
                options={Object.values(MaritalStatusEnum)
                  .filter((status) => status !== MaritalStatusEnum.PACS)
                  .map((status) => ({
                    label: t(maritalStatusEnumMapping[status]),
                    value: status
                  }))}
                selectedOption={getSelectedEnumOption(field.value, maritalStatusEnumMapping, t)}
              />
            </FormControl>
          </FormItem>
        )}
      />
    </InnerBox>
  )
}

export default HostFamilyParentsInformationForm
